import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Share2, Trash2, Clock, Download, RefreshCw, Loader2, Plus, ShieldCheck,
  Copy, Check, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardShell from '../components/brand/DashboardShell';
import ShareFileModal from '../components/files/ShareFileModal';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MySharesPage — Iter 141 Family-Facing Share UI.
 *
 * A self-service page for anyone who has minted a signed download URL:
 *   - Lists every share the caller has issued (newest first)
 *   - Shows expiry, download count, revoked state, recipient email
 *   - "Revoke" button on each active share
 *   - "Share new document" button opens the ShareFileModal
 *
 * Route: `/me/shares` — mounted from App.js for any authenticated user.
 * The backend enforces tenancy — a caller only ever sees their own shares.
 */
export default function MySharesPage() {
  const { getAuthHeader } = useAuth();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [shareFileId, setShareFileId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/files/shares/mine`, getAuthHeader());
      setShares(data.shares || []);
    } catch (e) {
      toast.error('Could not load your shares.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Revoke this share link? Anyone who has it will lose access immediately.'))
      return;
    try {
      await axios.post(`${API}/files/shares/${shareId}/revoke`, {}, getAuthHeader());
      toast.success('Share revoked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revoke failed');
    }
  };

  const startNewShare = () => {
    const id = window.prompt(
      'Enter the file ID you want to share.\n\n' +
      'If you don\'t know the ID, open the file from its detail page and use the "Share" button there.'
    );
    if (!id) return;
    setShareFileId(id.trim());
    setShowModal(true);
  };

  return (
    <DashboardShell>
      <div className="max-w-5xl mx-auto py-8 space-y-6" data-testid="my-shares-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-cn-text-muted">
              File Shares
            </div>
            <h1 className="cn-display text-3xl text-cn-text mt-1">
              Your shared documents
            </h1>
            <p className="text-sm text-cn-text-muted mt-2 max-w-2xl">
              Every signed download link you&apos;ve created — expiry, download count, and
              revocation. Anyone with an active link can download the file until it
              expires or you revoke it here.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={load}
              className="px-3 py-2 rounded-lg text-xs text-cn-text-muted border border-cn-border hover:bg-cn-bg-soft flex items-center gap-1.5"
              data-testid="my-shares-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <button
              type="button"
              onClick={startNewShare}
              className="px-3 py-2 rounded-lg text-xs bg-cn-primary text-white font-medium hover:bg-cn-primary/90 flex items-center gap-1.5"
              data-testid="my-shares-new-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Share a new document
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-cn-text-muted text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading shares…
          </div>
        ) : shares.length === 0 ? (
          <EmptyState onNew={startNewShare} />
        ) : (
          <div className="space-y-3" data-testid="my-shares-list">
            {shares.map((s) => (
              <ShareRow key={s.id} share={s} onRevoke={handleRevoke} />
            ))}
          </div>
        )}
      </div>

      <ShareFileModal
        open={showModal}
        onClose={() => setShowModal(false)}
        fileId={shareFileId}
        fileName={null}
        onShared={() => {
          setShowModal(false);
          load();
        }}
      />
    </DashboardShell>
  );
}

function EmptyState({ onNew }) {
  return (
    <div className="cn-card p-12 text-center border-2 border-dashed border-cn-border" data-testid="my-shares-empty">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-cn-primary/10 text-cn-primary flex items-center justify-center mb-4">
        <Share2 className="w-6 h-6" />
      </div>
      <div className="cn-display text-lg text-cn-text mb-1">
        No shares yet
      </div>
      <div className="text-sm text-cn-text-muted mb-5 max-w-md mx-auto">
        Create a time-limited link to send a discharge packet, clinical note, or
        signed order to a family member. Links audit-log every download.
      </div>
      <button
        type="button"
        onClick={onNew}
        className="px-4 py-2 rounded-lg text-sm bg-cn-primary text-white font-medium hover:bg-cn-primary/90 inline-flex items-center gap-1.5"
        data-testid="my-shares-empty-new-btn"
      >
        <Plus className="w-4 h-4" />
        Share a document
      </button>
    </div>
  );
}

function ShareRow({ share, onRevoke }) {
  const [copied, setCopied] = useState(false);
  const status = share.revoked
    ? { label: 'Revoked', tone: 'red', icon: <AlertCircle className="w-3 h-3" /> }
    : !share.is_active
      ? { label: 'Inactive', tone: 'muted', icon: <Clock className="w-3 h-3" /> }
      : { label: 'Active', tone: 'green', icon: <ShieldCheck className="w-3 h-3" /> };

  const tonePalette = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red:   'bg-red-50 border-red-200 text-red-700',
    muted: 'bg-cn-bg-soft border-cn-border text-cn-text-muted',
  }[status.tone];

  const expiresPretty = share.expires_at
    ? new Date(share.expires_at).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : '—';

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(share.id);
      setCopied(true);
      toast.success('Share ID copied');
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  return (
    <div className="cn-card p-4 flex items-start gap-4" data-testid={`my-shares-row-${share.id}`}>
      <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center">
        <Share2 className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-medium text-cn-text truncate max-w-[280px]">
            File · <span className="font-mono text-xs">{share.file_id}</span>
          </div>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${tonePalette}`}>
            {status.icon}{status.label}
          </span>
        </div>
        <div className="text-xs text-cn-text-muted mt-1 flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> expires {expiresPretty}
          </span>
          <span className="inline-flex items-center gap-1">
            <Download className="w-3 h-3" /> {share.download_count}/{share.max_downloads} downloaded
          </span>
          {share.recipient_email && (
            <span className="inline-flex items-center gap-1">
              → {share.recipient_email}
            </span>
          )}
        </div>
        {share.note && (
          <div className="text-xs text-cn-text-muted mt-1 italic">
            &quot;{share.note}&quot;
          </div>
        )}
      </div>
      <div className="flex-shrink-0 flex items-center gap-2">
        <button
          type="button"
          onClick={copyId}
          className="p-2 rounded-lg text-cn-text-muted hover:bg-cn-bg-soft"
          title="Copy share id"
          data-testid={`my-shares-copy-${share.id}`}
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        {share.is_active && !share.revoked && (
          <button
            type="button"
            onClick={() => onRevoke(share.id)}
            className="p-2 rounded-lg text-red-600 hover:bg-red-50"
            title="Revoke this share"
            data-testid={`my-shares-revoke-${share.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
